import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, MapPin, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { from_location: "", to_location: "", default_price: "", vehicle_type: "any", service_type: "transfert", notes: "" };

export default function PricingRoutes() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pricing-routes");
      setRows(data || []);
    } catch { toast.error("Échec du chargement"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({
    from_location: r.from_location, to_location: r.to_location,
    default_price: r.default_price, vehicle_type: r.vehicle_type || "any",
    service_type: r.service_type || "transfert", notes: r.notes || ""
  }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.from_location.trim() || !form.to_location.trim() || !form.default_price) {
      toast.error("Départ, arrivée et prix sont requis"); return;
    }
    setSaving(true);
    try {
      const body = { ...form, default_price: Number(form.default_price) };
      if (editing) await api.put(`/admin/pricing-routes/${editing.id}`, body);
      else await api.post("/admin/pricing-routes", body);
      toast.success(editing ? "Tarif mis à jour" : "Tarif créé");
      setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Supprimer "${r.from_location} → ${r.to_location}" ?`)) return;
    try { await api.delete(`/admin/pricing-routes/${r.id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Échec"); }
  };

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  return (
    <div data-testid="pricing-routes-page" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Button data-testid="pricing-back" variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-lg font-medium text-white flex items-center gap-2 flex-1">
            <MapPin size={20} className="text-amber-500" /> Tarifs par trajet
          </h1>
          <Button data-testid="add-route-btn" onClick={openCreate} className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-9">
            <Plus size={16} weight="bold" className="mr-1" /> Nouveau trajet
          </Button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
        {rows.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <p>Aucun tarif prédéfini.</p>
            <p className="text-xs mt-2">Créez des trajets récurrents (ex. CDG ↔ Paris, Orly ↔ Disneyland) pour auto-remplir les prix.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map(r => (
              <div key={r.id} data-testid={`route-row-${r.id}`} className="bg-[#121212] border border-zinc-800 rounded-lg px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{r.from_location} → {r.to_location}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    <span className="text-amber-500 font-semibold mr-2">{r.default_price} EUR</span>
                    <span className="uppercase mr-2">{r.vehicle_type || "any"}</span>
                    <span className="uppercase">{(r.service_type || "transfert").replace("_", " ")}</span>
                    {r.notes ? <span className="ml-2 text-zinc-600">· {r.notes}</span> : null}
                  </p>
                </div>
                <Button data-testid={`edit-route-${r.id}`} variant="ghost" size="sm" onClick={() => openEdit(r)} className="text-zinc-400 hover:text-white hover:bg-zinc-800"><PencilSimple size={15} /></Button>
                <Button data-testid={`del-route-${r.id}`} variant="ghost" size="sm" onClick={() => del(r)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash size={15} /></Button>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le tarif" : "Nouveau tarif"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} data-testid="route-form" className="space-y-3">
            <Field label="Lieu de départ *"><Input data-testid="rf-from" required value={form.from_location} onChange={setF("from_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
            <Field label="Lieu d'arrivée *"><Input data-testid="rf-to" required value={form.to_location} onChange={setF("to_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prix par défaut (EUR) *"><Input data-testid="rf-price" required type="number" min="0" step="0.5" value={form.default_price} onChange={setF("default_price")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
              <Field label="Véhicule">
                <Select value={form.vehicle_type} onValueChange={(v) => setForm(p => ({ ...p, vehicle_type: v }))}>
                  <SelectTrigger data-testid="rf-vehicle" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-zinc-700 text-white">
                    <SelectItem value="any">Tous</SelectItem>
                    <SelectItem value="eco">Eco</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="moto">Moto</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Type de service">
              <Select value={form.service_type} onValueChange={(v) => setForm(p => ({ ...p, service_type: v }))}>
                <SelectTrigger data-testid="rf-service" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-zinc-700 text-white">
                  <SelectItem value="transfert">Transfert</SelectItem>
                  <SelectItem value="mise_a_disposition">Mise à disposition</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes"><Input data-testid="rf-notes" value={form.notes} onChange={setF("notes")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">Annuler</Button>
              <Button data-testid="rf-save" type="submit" disabled={saving} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600">
                {saving ? "..." : editing ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-zinc-400 uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
